import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAutopilotConnectorCredentials,
  fetchAutopilotConnectorCredentialDetail,
  upsertAutopilotConnectorCredential,
  type AutopilotConnectorCredentialSummary,
  type AutopilotConnectorCredentialDetail,
} from '@/lib/api/autopilot';
import { useTenantSlug } from '@/stores/tenantStore';

export const useAutopilotConnectorCredentials = (
  environment: 'sandbox' | 'production' = 'production',
) => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'credentials', tenantSlug, environment],
    enabled: Boolean(tenantSlug),
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        return Promise.resolve([] as AutopilotConnectorCredentialSummary[]);
      }
      return fetchAutopilotConnectorCredentials({ tenantSlug, environment, signal });
    },
  });
};

export const useAutopilotConnectorCredentialDetail = (
  connector: AutopilotConnectorCredentialSummary['connector'] | null,
  environment: 'sandbox' | 'production' = 'production',
) => {
  const tenantSlug = useTenantSlug();
  return useQuery({
    queryKey: ['autopilot', 'credentials', tenantSlug, connector, environment],
    enabled: Boolean(tenantSlug && connector),
    queryFn: ({ signal }) => {
      if (!tenantSlug || !connector) {
        return Promise.resolve(null as AutopilotConnectorCredentialDetail | null);
      }
      return fetchAutopilotConnectorCredentialDetail({ tenantSlug, connector, environment, signal });
    },
  });
};

export const useUpsertAutopilotConnectorCredential = () => {
  const tenantSlug = useTenantSlug();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      connector,
      environment = 'production',
      credentials,
    }: {
      connector: AutopilotConnectorCredentialSummary['connector'];
      environment?: 'sandbox' | 'production';
      credentials: Record<string, unknown>;
    }) => {
      if (!tenantSlug) {
        throw new Error('Tenant slug required');
      }
      await upsertAutopilotConnectorCredential({ tenantSlug, connector, environment, credentials });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['autopilot', 'credentials'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['autopilot', 'credentials', variables.connector],
      });
    },
  });
};
